import "./section-tabs.css";

export class SectionTabs {
  private readonly listenerController = new AbortController();
  private activeTab = "";

  constructor(private readonly root: HTMLElement) {
    this.activeTab =
      root.querySelector<HTMLButtonElement>(
        '[data-section-tab][aria-selected="true"]',
      )?.dataset.sectionTab ??
      root.querySelector<HTMLButtonElement>("[data-section-tab]")?.dataset
        .sectionTab ??
      "";

    root.addEventListener("click", this.handleClick, {
      signal: this.listenerController.signal,
    });
    root.addEventListener("keydown", this.handleKeydown, {
      signal: this.listenerController.signal,
    });
    this.sync();
  }

  destroy(): void {
    this.listenerController.abort();
  }

  sync(): void {
    const tabs = [
      ...this.root.querySelectorAll<HTMLButtonElement>("[data-section-tab]"),
    ];
    if (!tabs.some((tab) => tab.dataset.sectionTab === this.activeTab)) {
      this.activeTab = tabs[0]?.dataset.sectionTab ?? "";
    }

    for (const tab of tabs) {
      const selected = tab.dataset.sectionTab === this.activeTab;
      tab.setAttribute("aria-selected", String(selected));
      tab.tabIndex = selected ? 0 : -1;
    }
    for (const panel of this.root.querySelectorAll<HTMLElement>(
      "[data-section-panel]",
    )) {
      panel.hidden = panel.dataset.sectionPanel !== this.activeTab;
    }
  }

  private activate(tabId: string | undefined, focus: boolean): void {
    if (
      !tabId ||
      ![...this.root.querySelectorAll<HTMLButtonElement>("[data-section-tab]")]
        .some((tab) => tab.dataset.sectionTab === tabId)
    ) {
      return;
    }

    this.activeTab = tabId;
    this.sync();
    if (focus) {
      [...this.root.querySelectorAll<HTMLButtonElement>("[data-section-tab]")]
        .find((tab) => tab.dataset.sectionTab === tabId)
        ?.focus();
    }
  }

  private handleClick = (event: MouseEvent): void => {
    if (!(event.target instanceof Element)) return;

    const tab = event.target.closest<HTMLButtonElement>("[data-section-tab]");
    if (tab && this.root.contains(tab)) {
      this.activate(tab.dataset.sectionTab, false);
      return;
    }

    const target = event.target.closest<HTMLButtonElement>(
      "[data-section-tab-target]",
    );
    if (target && this.root.contains(target)) {
      this.activate(target.dataset.sectionTabTarget, true);
    }
  };

  private handleKeydown = (event: KeyboardEvent): void => {
    const target = event.target;
    if (
      !(target instanceof HTMLButtonElement) ||
      target.getAttribute("role") !== "tab" ||
      !this.root.contains(target)
    ) {
      return;
    }

    const tabs = [
      ...this.root.querySelectorAll<HTMLButtonElement>("[data-section-tab]"),
    ];
    const currentIndex = tabs.indexOf(target);
    if (currentIndex < 0) return;

    const nextIndex =
      event.key === "ArrowRight"
        ? (currentIndex + 1) % tabs.length
        : event.key === "ArrowLeft"
          ? (currentIndex - 1 + tabs.length) % tabs.length
          : event.key === "Home"
            ? 0
            : event.key === "End"
              ? tabs.length - 1
              : -1;
    if (nextIndex < 0) return;

    event.preventDefault();
    this.activate(tabs[nextIndex]?.dataset.sectionTab, true);
  };
}
